import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Analytics from '@lib/analytics';
import Calculator from './calculator';
import Illustration from '@components/finance/illustration';

const FinanceTypes = ({
  financeType,
  openModal,
  calculator,
  illustration,
  hpCalculatorChanged,
  pcpCalculatorChanged,
  dealTotal,
  isDepositSaved,
  loading,
  pcpError,
}) => {
  const [isShowKeyFeatures, setIsShowKeyFeatures] = useState(false);

  const financeTypes = {
    personal_contract_hire: {
      title: 'Personal contract hire',
      acronym: '(PCH)',
      features: [
        'Low deposits',
        'Fixed rentals for the duration of the contract',
        'You won’t own the car at the end of the agreement',
      ],
    },
    contract_hire: {
      title: 'Business contract hire',
      acronym: '(BCH)',
      features: [
        'Low deposits',
        'Fixed rentals for the duration of the contract',
        'Suitable for businesses of all sizes - contact us for full details',
      ],
    },
    pcp: {
      title: 'Personal contract purchase',
      acronym: '(PCP)',
      features: [
        'Monthly payments are usually lower',
        'Hand the car back or trade it in',
        'Option to pay the final balloon payment to keep the vehicle',
        'Normally shorter contracts',
        'Mileage restrictions',
      ],
    },
    hp: {
      title: 'Hire purchase',
      acronym: '(HP)',
      features: [
        'Typically higher monthly payments',
        'You’ll own the vehicle after all payments are made',
        'Normally 5 year contracts',
        'No mileage restrictions',
      ],
    },
  };

  const renderFinance = () => {
    if (pcpError && financeType === 'pcp') {
      return (
        <div className="ch-pa--3 md:ch-pa--4 sl:ch-width--6 ch-rounded--md ac-finance__error-card">
          <p className="ch-fw--500 ch-fs--4 md:ch-fs--5 ac-finance__error-message">
            {pcpError}
          </p>
          <p className="ch-fs--3 ch-mb--0">
            Please contact us{' '}
            <span className="sm:ch-display--none">
              via web chat or WhatsApp{' '}
            </span>
            for more information.
          </p>
        </div>
      );
    }

    return (
      <div
        className={`ch-mt--3 sl:ch-mt--0 ch-relative ${
          calculator ? 'sl:ch-width--6' : 'sl:ch-width--12'
        }`}
      >
        {loading && (
          <div className="ac-loading__wrapper">
            <span className="ac-loading__spinner" />
            <p className="ac-loading__text">Waiting for finance quote</p>
          </div>
        )}
        <Illustration financeData={illustration} />
      </div>
    );
  };

  const toggleShowKeyFeatures = () => {
    Analytics.trackEvent('interaction', {
      action: `${
        isShowKeyFeatures ? 'Closed' : 'Opened'
      } finance key features list`,
      label: financeType,
    });
    setIsShowKeyFeatures(!isShowKeyFeatures);
  };

  const openFinanceModal = () => {
    openModal();
    return Analytics.trackEvent('interaction', {
      action: 'Opened finance key features modal',
      label: financeType,
    });
  };

  return (
    <>
      <div className="ch-card ch-mb--2">
        <div className="ch-card__content">
          <h2 className="ch-fs--4 md:ch-fs--5">
            {financeTypes[financeType]['title']}{' '}
            <span className="ch-fw--400">
              {financeTypes[financeType]['acronym']}
            </span>
          </h2>
          <p className="ch-color--grey-6 ch-fs--3">
            This is an example, we’ll always discuss your options in detail
            before you choose.
          </p>
          <button
            className={`js-off--hide ch-btn ch-btn--sm ch-mb--2 ac-finance__features-toggle ${
              isShowKeyFeatures ? 'ac-finance__features-toggle--open' : ''
            }`}
            onClick={toggleShowKeyFeatures}
            type="button"
          >
            {isShowKeyFeatures ? 'Close' : 'Show'} key features
          </button>
          {isShowKeyFeatures && (
            <div>
              <ul className="ch-list ch-list--tall ch-measure ch-mb--2">
                {financeTypes[financeType]['features'].map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              {(financeType === 'pcp' || financeType === 'hp') && (
                <button
                  className="ch-btn ch-btn--link ch-fs--2 ch-ph--0"
                  onClick={openFinanceModal}
                  type="button"
                >
                  Read about how {financeType === 'pcp' ? 'PCP' : 'HP'} works
                </button>
              )}
            </div>
          )}
        </div>
        <div className="ch-card__content ch-card__content--grey ch-display--flex ch-flex-flow--column-nowrap sl:ch-flex-flow--row-nowrap">
          {calculator && (
            <div className="sl:ch-width--6 sl:ch-pr--3">
              <Calculator
                {...calculator}
                calculatorChanged={
                  financeType === 'pcp'
                    ? pcpCalculatorChanged
                    : hpCalculatorChanged
                }
                dealTotal={dealTotal}
                financeType={financeType}
                isDepositSaved={isDepositSaved}
                key={financeType}
              />
            </div>
          )}
          {renderFinance()}
        </div>
      </div>
    </>
  );
};

export default FinanceTypes;

FinanceTypes.propTypes = {
  calculator: PropTypes.object,
  dealTotal: PropTypes.number,
  financeType: PropTypes.string,
  hpCalculatorChanged: PropTypes.func,
  illustration: PropTypes.object,
  isDepositSaved: PropTypes.bool,
  loading: PropTypes.bool,
  openModal: PropTypes.func,
  pcpCalculatorChanged: PropTypes.func,
  pcpError: PropTypes.string,
};
